import React, { useEffect, useState, useImperativeHandle } from "react";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils";

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York",
  "¥": "Asia/Tokyo",
  "Rs.": "Asia/Colombo",
  "€": "Europe/Berlin",
  "£": "Europe/London",
  "₹": "Asia/Kolkata",
  A$: "Australia/Sydney",
  C$: "America/Toronto",
};

interface KotReceiptProps {
  orderNumber: string;
  tableNumber: number;
  peopleCount: number;
  items: any[];
  shouldPrint?: boolean;
}

const KotReceipt = React.forwardRef<any, KotReceiptProps>(
  (
    { orderNumber, tableNumber, peopleCount, items, shouldPrint = false },
    ref,
  ) => {
    const shopName = getDecryptedToken("martName");
    const currency = getDecryptedToken("currency");
    const serverName = getDecryptedToken("name");

    const [currentTime, setCurrentTime] = useState(new Date());

    const timeZone =
      currencyTimeZoneMap[currency as string] ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    useEffect(() => {
      const timer = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => clearInterval(timer);
    }, []);

    // Function to generate and print KOT receipt
    const generateAndPrint = () => {
      // Build items HTML
      const itemsHtml = (items || [])
        .map((item: any) => {
          const noteHtml = item?.note
            ? `<div class="kot-note">Note: ${(item.note || "").replace(/</g, "&lt;")}</div>`
            : "";
          return `
            <div class="kot-item">
              <div class="kot-item-row">
                <span class="kot-qty">${item?.quantity || 0}x</span>
                <span class="kot-name">${(item?.name || "").replace(/</g, "&lt;")}</span>
              </div>
              ${noteHtml}
            </div>
          `;
        })
        .join("");

      // Date/time HTML
      const dateTimeHtml = `${currentTime.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone,
      })} ${currentTime.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone,
      })}`;

      const kotHtml = `
        <!doctype html>
        <html>
        <head>
        <meta charset="utf-8"/>
        <title>KOT Receipt</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0;
            padding: 0;
          }
          * {
            margin: 0;
            padding: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }
          .kot-receipt {
            background-color: white;
            color: black;
            width: 350px;
            margin: 0;
            padding: 5px 8px;
            box-sizing: border-box;
            display: block;
            font-family: Arial, sans-serif;
            text-transform: uppercase;
          }
          .kot-receipt > div {
            padding: 2px 0;
          }
          .kot-receipt hr {
            border: 1px dotted;
            margin: 5px 0;
          }
          .kot-headers {
            background-color: white;
            color: black;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .kot-title {
            font-size: 40px;
            font-weight: 700;
            text-align: center;
            word-wrap: break-word;
            width: 100%;
            overflow-wrap: break-word;
          }
          .kot-subtitle {
            font-size: 14px;
            font-weight: 700;
            text-align: center;
            margin-top: 5px;
          }
          .kot-order-info {
            width: 100%;
            text-align: center;
            margin-top: 8px;
            font-size: 12px;
          }
          .kot-order-number,
          .kot-table,
          .kot-guests {
            margin: 3px 0;
          }
          .kot-time {
            font-size: 12px;
            text-align: center;
            margin-top: 5px;
          }
          .kot-heading {
            display: flex;
            justify-content: space-between;
            gap: 1px;
            font-size: 12px;
            font-weight: 700;
            padding: 2px 3px;
          }
          .kot-item {
            padding: 1px 3px;
            font-size: 13px;
          }
          .kot-item-row {
            display: flex;
            gap: 1px;
            margin-bottom: 3px;
          }
          .kot-qty {
            min-width: 30px;
            font-weight: 700;
          }
          .kot-name {
            flex: 1;
          }
          .kot-note {
            font-size: 10px;
            margin-left: 35px;
            margin-top: 2px;
            font-style: italic;
          }
          .kot-footer {
            text-align: center;
            font-size: 12px;
            padding: 10px;
          }
          @media print {
            @page {
              size: 80mm auto;
              margin: 0 !important;
              padding: 0 !important;
            }
            * {
              margin: 0 !important;
              padding: 0 !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 100% !important;
              display: flex !important;
              justify-content: center !important;
              align-items: flex-start !important;
            }
            .kot-receipt {
              width: 80mm !important;
              min-width: 80mm !important;
              max-width: 80mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              box-shadow: none !important;
              display: block !important;
            }
            .kot-receipt {
              padding: 3px 5px !important;
            }
            .kot-receipt > div {
              padding: 2px 0 !important;
            }
            .kot-title {
              font-size: 28px !important;
              line-height: 1.1 !important;
            }
            .kot-item-row,
            .kot-note,
            .kot-heading {
              white-space: nowrap !important;
              flex-wrap: nowrap !important;
              font-size: 12px !important;
            }
            .kot-qty,
            .kot-name {
              white-space: nowrap !important;
              flex-shrink: 0 !important;
            }
          }
        </style>
        </head>
        <body>
        <div class="kot-receipt">
          <div class="kot-headers">
            <div class="kot-title">${(shopName || "").replace(/</g, "&lt;")}</div>
            <div class="kot-subtitle">KITCHEN ORDER TICKET</div>
            <div class="kot-order-info">
              <div class="kot-order-number">Order: ${(orderNumber || "").replace(/</g, "&lt;")}</div>
              <div class="kot-table">Table: ${tableNumber}</div>
              <div class="kot-guests">Guests: ${peopleCount}</div>
            </div>
            <div class="kot-time">${dateTimeHtml}</div>
          </div>
          <hr />
          <div class="kot-heading">
            <span>QTY</span>
            <span>ITEM</span>
          </div>
          <hr />
          ${itemsHtml}
          <hr />
          <div class="kot-footer">
            <div class="kot-server">Server: ${(serverName || "").replace(/</g, "&lt;")}</div>
          </div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              // Trigger print dialog
              window.print();
              // Close window after print (works differently on different browsers)
              window.onafterprint = function() {
                window.close();
              };
              // Fallback close after 2 seconds if onafterprint doesn't work
              setTimeout(function() {
                window.close();
              }, 2000);
            }, 150);
          };
        </script>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank", "width=400,height=800");
      if (!printWindow) {
        console.error("Unable to open print window. Please allow popups.");
        return;
      }
      printWindow.document.open();
      printWindow.document.write(kotHtml);
      printWindow.document.close();
    };

    // For auto-print via shouldPrint prop (future use)
    useEffect(() => {
      if (shouldPrint && typeof generateAndPrint === "function") {
        const timer = setTimeout(() => {
          generateAndPrint();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [shouldPrint]);

    // Expose print function via useImperativeHandle
    useImperativeHandle(ref, () => ({
      print: generateAndPrint,
    }));

    return null;
  },
);

KotReceipt.displayName = "KotReceipt";

export default KotReceipt;
